/**
 * How a ledger line is NAMED — one table, used wherever `order_lines` are
 * shown to staff.
 *
 * It was the private top of the dashboard's Order Ledger (now
 * Orders/Components/OrderLedger.jsx, shown in Order Details' Order Ledger
 * tab). The dispatcher's trip screen now shows the
 * adjustments to an operator's fee too, and the whole point of that screen is
 * that a line reads the SAME on a phone as it does on the dashboard — so the
 * naming moved here and both import it.
 *
 * WHAT DID NOT MOVE, and cannot: the ledger itself. It is a 1 200-line
 * react-bootstrap table fed by `bookings/order-lines` and the booking's
 * payments through the admin client, inside an admin modal that edits it. None
 * of that exists in the portal, which is a separate mobile shell with its own
 * components and its own money formatting (`operatorFormat.formatMoney` —
 * '9 000 Ft', grouped the way the driver screens group it, against the
 * dashboard's `€1,234.00`). So what is shared is the part that is genuinely
 * the same question — what do we CALL this line — and each surface renders
 * and formats it in its own idiom.
 *
 * `variant` and `icon` are react-bootstrap's, and only the dashboard reads
 * them; they stay here so there is one row per kind rather than two tables
 * that can disagree about which kinds exist.
 */

import { OPERATOR_ROLE_LABELS } from '../config/bookingConstants';

/** Display config per kind. The order of the keys is the dashboard's render order. */
export const ORDER_LINE_KINDS = {
  base: {
    label: 'Base price', variant: 'primary', icon: 'fa-tag', readOnly: true,
  },
  service: {
    label: 'Service', variant: 'success', icon: 'fa-gift', readOnly: true,
  },
  operator: {
    label: 'Operator', variant: 'info', icon: 'fa-user-tie', readOnly: true,
  },
  cash_from_client: {
    label: 'Cash collected', variant: 'warning', icon: 'fa-money-bill', readOnly: true,
  },
  partner_cancellation: {
    label: 'Cancellation (partner)', variant: 'secondary', icon: 'fa-times-circle', readOnly: true,
  },
  operator_cancellation: {
    label: 'Cancellation (operator)', variant: 'secondary', icon: 'fa-times-circle', readOnly: true,
  },
  adjustment: {
    label: 'Adjustment', variant: 'dark', icon: 'fa-pen', readOnly: false,
  },
};

/** The order the dashboard's ledger renders its sections in. */
export const ORDER_LINE_KIND_ORDER = [
  'base', 'service', 'operator', 'cash_from_client',
  'partner_cancellation', 'operator_cancellation', 'adjustment',
];

/**
 * The config for one line's kind. A line with no kind, or one written by a
 * newer server than this build knows about, reads as an adjustment — the
 * same fallback the ledger has always used for pre-migration rows, so a line
 * is never nameless.
 */
export const orderLineKind = (kind) => ORDER_LINE_KINDS[kind] || ORDER_LINE_KINDS.adjustment;

/** 'Adjustment' — what to call this line. */
export const orderLineKindLabel = (kind) => orderLineKind(kind).label;

/**
 * 'Driver' — a slot's role as the dashboard's role table names it, so the
 * cost role reads 'Cost' here as it does on every badge. A role the table
 * does not know is shown capitalised rather than dropped. Nothing for no
 * role.
 */
export function formatOrderLineRole(role) {
  if (!role) return null;

  return OPERATOR_ROLE_LABELS[role]?.label || role.charAt(0).toUpperCase() + role.slice(1);
}
